import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Editor, { useMonaco, OnMount } from "@monaco-editor/react";
import type * as Monaco from "monaco-editor";
import { Check, ChevronLeft, ChevronRight, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { inferFormulaWarnings, parseAndResolve } from "@brewbase/lib";
import type { ResolutionError } from "@brewbase/lib";
import {
  LANGUAGE_ID,
  THEME_ID,
  registerFormulaLanguage,
  setDiagnostics,
  setModelContext,
  setModelScope,
  setModelLoading,
} from "./language";
import { useFormulaContext } from "./hooks/useFormulaContext";
import { useFormulaEvalScope } from "./formula-eval-scope-context";
import { useNestedFormula } from "./nested-formula-context";
import { cn } from "@/lib/utils";
import { UpstreamChips } from "./UpstreamChips";
import { Palette } from "./palette/Palette";
import type { FormulaContextInput } from "./context";

/** Monaco's built-in snippet contribution — used for tabstop-aware inserts. */
interface SnippetController extends Monaco.editor.IEditorContribution {
  insert(template: string): void;
}

interface FullscreenFormulaProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onSave: (value: string) => void | Promise<void>;
  context: FormulaContextInput;
  title?: string;
}

export function FullscreenFormula({
  open,
  onOpenChange,
  value,
  onSave,
  context,
  title = "Formula editor",
}: FullscreenFormulaProps) {
  const monaco = useMonaco();
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const formulaContext = useFormulaContext(open ? context : null);

  const isDirty = draft !== value;

  // Live-evaluation scope (harvest editors with preview data); null elsewhere.
  const evalScopeApi = useFormulaEvalScope();
  const [editorReady, setEditorReady] = useState(false);
  const [tabularRowIndex, setTabularRowIndex] = useState<number | undefined>(
    undefined,
  );
  const tabularAttributeId =
    context.kind === "harvest-tabular-column"
      ? context.templateAttributeId
      : undefined;
  const tabularRowCount =
    tabularAttributeId !== undefined && evalScopeApi
      ? evalScopeApi.getTabularRowCount(tabularAttributeId)
      : 0;
  // undefined → last row (meaningful prev/elapsed).
  const effectiveRowIndex = tabularRowIndex ?? Math.max(0, tabularRowCount - 1);

  // Rebuild from a fresh sessionStorage snapshot when the dialog opens or the
  // chosen tabular row changes. `JSON.stringify(context)` keeps a new inline
  // context object from re-triggering every render.
  const contextKey = JSON.stringify(context);
  const evalScope = useMemo(
    () =>
      open && evalScopeApi
        ? evalScopeApi.buildScope(context, { tabularRowIndex: effectiveRowIndex })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, evalScopeApi, contextKey, effectiveRowIndex],
  );

  // The result is "computing" while the context loads, or (when a value is
  // expected) while the scope's underlying data is still being fetched.
  const loading =
    (open && !formulaContext) ||
    (evalScope != null && (evalScopeApi?.isLoading ?? false));

  // Reset draft when opening
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  // Register language
  useEffect(() => {
    if (!monaco) return;
    registerFormulaLanguage(monaco);
  }, [monaco]);

  // Bind context to model
  useEffect(() => {
    const ed = editorRef.current;
    if (!ed) return;
    const model = ed.getModel();
    if (!model) return;
    const uri = model.uri.toString();
    setModelContext(uri, formulaContext);
    return () => setModelContext(uri, null);
  }, [formulaContext]);

  // Bind live-value scope to model (drives the result inlay + value hover).
  // `editorReady` covers both orderings: scope may resolve before or after mount.
  useEffect(() => {
    const model = editorRef.current?.getModel();
    if (!model) return;
    const uri = model.uri.toString();
    setModelScope(uri, evalScope);
    return () => setModelScope(uri, null);
  }, [evalScope, editorReady]);

  // Surface a `→ …` loading hint while the context/scope data is being fetched.
  useEffect(() => {
    const model = editorRef.current?.getModel();
    if (!model) return;
    const uri = model.uri.toString();
    setModelLoading(uri, loading);
    return () => setModelLoading(uri, false);
  }, [loading, editorReady]);

  // Diagnostics
  useEffect(() => {
    if (!monaco) return;
    const ed = editorRef.current;
    if (!ed) return;
    const model = ed.getModel();
    if (!model) return;
    setDiagnostics(monaco, model, formulaContext);
  }, [monaco, draft, formulaContext]);

  const handleMount = useCallback<OnMount>(
    (editor, monacoInstance) => {
      editorRef.current = editor;
      monacoInstance.editor.setTheme(THEME_ID);
      const model = editor.getModel();
      if (model) {
        setModelContext(model.uri.toString(), formulaContext);
        setDiagnostics(monacoInstance, model, formulaContext);
      }
      setEditorReady(true);
    },
    [formulaContext],
  );

  const insertAtCursor = useCallback((snippet: string) => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.focus();
    // Insert through Monaco's snippet engine so tabstops/placeholders work:
    // `$0` is the final caret, `${1:name}` are Tab-navigable argument
    // placeholders (the palette emits these for function calls).
    const controller = ed.getContribution<SnippetController>(
      "snippetController2",
    );
    if (controller) {
      controller.insert(snippet);
    } else {
      const sel = ed.getSelection();
      if (sel) {
        ed.executeEdits("palette-insert", [
          { range: sel, text: snippet.replace(/\$\{?\d+:?([^}]*)\}?/g, "$1"), forceMoveMarkers: true },
        ]);
      }
    }
    // Pop completions when the snippet sets up a continuation — a trailing dot
    // (column member access) or an explicit `$0` caret slot (a predicate/column
    // argument). Argument placeholders (`${1:…}`) intentionally don't.
    if (snippet.includes("$0") || snippet.endsWith(".")) {
      ed.trigger("palette", "editor.action.triggerSuggest", {});
    }
  }, []);

  /** Selects and reveals a source span, for jump-to-error from the footer. */
  const jumpToOffset = useCallback((start: number, end: number) => {
    const ed = editorRef.current;
    const model = ed?.getModel();
    if (!ed || !model) return;
    const from = model.getPositionAt(start);
    const to = model.getPositionAt(end);
    ed.setSelection({
      startLineNumber: from.lineNumber,
      startColumn: from.column,
      endLineNumber: to.lineNumber,
      endColumn: to.column,
    });
    ed.revealPositionInCenter(from);
    ed.focus();
  }, []);

  const diagnostics = useMemo<
    | {
        phase: "parse";
        kind: "incomplete" | "malformed";
        message: string;
        index?: number;
      }
    | { phase: "resolve"; errors: ResolutionError[] }
    | null
  >(() => {
    if (!formulaContext || !draft.trim()) return null;
    const result = parseAndResolve(draft, formulaContext);
    if (result.ok) return null;
    if (result.phase === "parse")
      return {
        phase: "parse",
        kind: result.kind,
        message: result.message,
        index: result.index,
      };
    return { phase: "resolve", errors: result.errors };
  }, [draft, formulaContext]);

  // Shown only when there is nothing worse to report: the formula works, but
  // the author should check it (e.g. mL added to µL as entered).
  const warnings = useMemo(
    () =>
      formulaContext && draft.trim()
        ? inferFormulaWarnings(draft, formulaContext)
        : [],
    [draft, formulaContext],
  );

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(draft);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  /** Close requests (Escape, outside click, Cancel) route through here so we
   *  can guard unsaved edits. */
  const requestClose = useCallback(() => {
    if (isDirty) setConfirmDiscard(true);
    else onOpenChange(false);
  }, [isDirty, onOpenChange]);

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (next) onOpenChange(true);
      else requestClose();
    },
    [onOpenChange, requestClose],
  );

  const discardAndClose = useCallback(() => {
    setConfirmDiscard(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const showUpstreamChips =
    context.kind === "harvest-attribute" || context.kind === "harvest-kpi";

  const nested = useNestedFormula();

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className={cn(
            "flex flex-col max-w-none max-h-none p-0",
            nested
              ? "w-[calc(100vw-12rem)] h-[calc(100vh-12rem)]"
              : "w-[calc(100vw-4rem)] h-[calc(100vh-4rem)]",
          )}
        >
          <DialogHeader className="flex-none px-4 py-2 border-b border-olive-6">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 flex">
            <div className="flex-1 min-w-0 flex flex-col">
              {context.kind === "harvest-tabular-column" && evalScopeApi && (
                <TabularRowPicker
                  rowCount={tabularRowCount}
                  index={effectiveRowIndex}
                  onChange={setTabularRowIndex}
                />
              )}
              <DiagnosticsBar
                diagnostics={diagnostics}
                warnings={warnings}
                onJump={jumpToOffset}
              />
              <div className="flex-1 min-h-0">
                <Editor
                  value={draft}
                  onChange={(v) => setDraft(v ?? "")}
                  language={LANGUAGE_ID}
                  onMount={handleMount}
                  options={{
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    fontSize: 13,
                    minimap: { enabled: false },
                    lineNumbers: "on",
                    wordWrap: "on",
                    tabSize: 2,
                    scrollBeyondLastLine: false,
                    padding: { top: 8, bottom: 8 },
                  }}
                  height="100%"
                  width="100%"
                />
              </div>
              {showUpstreamChips && "templateId" in context && (
                <div className="flex-none px-4 py-2 border-t border-olive-6">
                  <UpstreamChips
                    templateId={context.templateId}
                    formula={draft}
                    onInsert={(code) => insertAtCursor(`upstream.${code}`)}
                  />
                </div>
              )}
            </div>
            <div className="w-96 flex-none">
              {formulaContext ? (
                <Palette context={formulaContext} onInsert={insertAtCursor} />
              ) : (
                <div className="p-3 text-sm text-olive-10">
                  Loading context…
                </div>
              )}
            </div>
          </div>
          <div className="flex-none px-4 py-2 border-t border-olive-6 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={requestClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={saving || diagnostics !== null || !isDirty}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              This formula has unsaved edits. Closing the editor will discard
              them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={discardAndClose}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Picks which dummy preview row a tabular-column formula is evaluated against —
 * the row drives `row`/`prev`/`elapsed` for the live result and value hover.
 * Defaults to the last row (so `prev`/`elapsed` are meaningful).
 */
function TabularRowPicker({
  rowCount,
  index,
  onChange,
}: {
  rowCount: number;
  index: number;
  onChange: (index: number) => void;
}) {
  if (rowCount === 0) {
    return (
      <div className="flex-none px-4 py-1.5 border-b border-olive-6 text-xs text-olive-10">
        Add preview rows to see live values.
      </div>
    );
  }
  return (
    <div className="flex-none flex items-center gap-2 px-4 py-1.5 border-b border-olive-6 text-xs text-olive-10">
      <span>Preview row</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="text-olive-10 hover:text-olive-12 disabled:opacity-40"
          disabled={index <= 0}
          onClick={() => onChange(Math.max(0, index - 1))}
          aria-label="Previous row"
        >
          <ChevronLeft size={14} />
        </button>
        <span className="font-mono text-olive-12">
          {index + 1} / {rowCount}
        </span>
        <button
          type="button"
          className="text-olive-10 hover:text-olive-12 disabled:opacity-40"
          disabled={index >= rowCount - 1}
          onClick={() => onChange(Math.min(rowCount - 1, index + 1))}
          aria-label="Next row"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

function DiagnosticsBar({
  diagnostics,
  warnings,
  onJump,
}: {
  /** Non-blocking findings, shown when the formula has no errors. */
  warnings: string[];
  diagnostics:
    | {
        phase: "parse";
        kind: "incomplete" | "malformed";
        message: string;
        index?: number;
      }
    | { phase: "resolve"; errors: ResolutionError[] }
    | null;
  /** Reveal + select a source span [start, end) in the editor. */
  onJump: (start: number, end: number) => void;
}) {
  if (!diagnostics && warnings.length > 0) {
    return (
      <div className="flex-none max-h-32 overflow-y-auto border-b border-amber-600/40 bg-warning/20">
        {warnings.map((w) => (
          <div
            key={w}
            className="flex items-start gap-1.5 px-4 py-1 text-xs text-warning-foreground"
          >
            <TriangleAlert size={12} className="shrink-0 mt-0.5" />
            <span>{w}</span>
          </div>
        ))}
      </div>
    );
  }

  if (!diagnostics) {
    return (
      <div className="flex-none flex items-center gap-1.5 px-4 py-1.5 border-b border-olive-6 text-xs text-olive-10">
        <Check size={12} className="text-success" />
        No issues
      </div>
    );
  }

  // Incomplete-while-typing is not an error — keep it calm and muted, with no
  // editor squiggle/lens (handled in setDiagnostics).
  if (diagnostics.phase === "parse" && diagnostics.kind === "incomplete") {
    return (
      <div className="flex-none flex items-center gap-1.5 px-4 py-1.5 border-b border-olive-6 text-xs text-olive-10">
        <span aria-hidden className="text-olive-9">
          ⋯
        </span>
        {diagnostics.message}
      </div>
    );
  }

  const rows =
    diagnostics.phase === "parse"
      ? [
          {
            path: undefined as string | undefined,
            message: diagnostics.message,
            onClick:
              diagnostics.index != null
                ? () => onJump(diagnostics.index!, diagnostics.index! + 1)
                : undefined,
          },
        ]
      : diagnostics.errors.map((e) => ({
          path: e.path,
          message: e.message,
          onClick: e.range
            ? () => onJump(e.range!.start, e.range!.end)
            : undefined,
        }));

  return (
    <div className="flex-none max-h-32 overflow-y-auto border-b border-tomato-6 bg-tomato-2 divide-y divide-tomato-4">
      {rows.map((r, i) => (
        <ErrorRow key={i} path={r.path} message={r.message} onClick={r.onClick} />
      ))}
    </div>
  );
}

function ErrorRow({
  path,
  message,
  onClick,
}: {
  path?: string;
  message: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <TriangleAlert size={12} className="shrink-0 mt-0.5 text-tomato-11" />
      <span className="font-mono">
        {path ? <span className="opacity-70">{path}: </span> : null}
        {message}
      </span>
    </>
  );
  const className =
    "flex items-start gap-1.5 w-full text-left px-4 py-1 text-xs text-tomato-11";
  if (!onClick) {
    return <div className={className}>{content}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(className, "hover:bg-tomato-3")}
      title="Jump to error"
    >
      {content}
    </button>
  );
}
